import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Input } from 'reactstrap';
import { LoiFormSection } from '../../slices/marketplace/loi-form/loi-form-section';
import { LegalIssueItem } from '../../slices/marketplace/loi-form/legal-issue-item';
import { LoiForm } from '../../slices/marketplace/loi-form/loi-form';
import {
    LoiFormAnswerType,
    LoiFormAnswerTypeList,
} from '../../slices/marketplace/loi-form/loi-form-answer-type.enum';
import { LoiFormMode } from '../../slices/marketplace/loi-form/loi-form-mode.enum';
import { getOrderedItemsForSection } from '../../slices/marketplace/loi-form/get-ordered-items-for-section';
import { TermForm } from '../../slices/marketplace/loi-form/term-form';
import { hasValueForLinkedField } from '../../slices/marketplace/loi-form/has-value-for-linked-field';
import { isLegalIssueVisibleByLogic } from '../../slices/marketplace/loi-form/legal-issue-visibility';
import InfoTooltip from '../info-tooltip/info-tooltip';
import CollapsibleComponent from '../Collapsible/CollapsibleComponent';
import './LegalIssuesForm.scss';

export interface LegalIssuesFormProps {
    legalIssueSections: LoiFormSection[];
    legalIssues: LegalIssueItem[];
    termForm?: TermForm | null;
    onChange?: (payload: LoiForm) => void;
    mode?: LoiFormMode;
    readOnly?: boolean;
    displayAsLabels?: boolean;
    invalidItemIds?: Set<string>;
}

export const LegalIssuesForm = (props: LegalIssuesFormProps) => {
    const {
        legalIssueSections: initialSections,
        legalIssues: initialIssues,
        termForm,
        onChange,
        mode,
        readOnly = false,
        displayAsLabels = false,
        invalidItemIds,
    } = props;
    const isAdminViewMode = mode === LoiFormMode.AdminDefaults;

    const sectionsToDisplay =
        termForm != null && mode === LoiFormMode.LoiSubmissionEvent
            ? initialSections.filter((s) => {
                  const linked = s.termsFormLinkedField;
                  if (linked == null) return true;
                  return hasValueForLinkedField(termForm, linked);
              })
            : initialSections;

    const [legalIssues, setLegalIssues] =
        useState<LegalIssueItem[]>(initialIssues);
    const [openSectionId, setOpenSectionId] = useState<string | null>(null);
    const skipSyncFromPropsRef = useRef(false);

    useEffect(() => {
        if (skipSyncFromPropsRef.current) {
            skipSyncFromPropsRef.current = false;
            return;
        }
        setLegalIssues(initialIssues);
    }, [initialIssues]);

    const notifyChange = useCallback(
        (next: LegalIssueItem[]) => {
            skipSyncFromPropsRef.current = true;
            queueMicrotask(() => {
                onChange?.({
                    legalIssueSections: initialSections,
                    legalIssues: next,
                });
            });
        },
        [initialSections, onChange]
    );

    const handleValueChange = useCallback(
        (itemId: string, value: string | null) => {
            setLegalIssues((prev) => {
                const next = prev.map((item) =>
                    item.id === itemId ? { ...item, value } : item
                );
                notifyChange(next);
                return next;
            });
        },
        [notifyChange]
    );

    const getSelectOptions = (item: LegalIssueItem) => {
        if (
            item.answerType !== LoiFormAnswerType.PurchaseAgreementType &&
            item.answerType !== LoiFormAnswerType.LeaseRateUponExtension
        ) {
            return item.options ?? [];
        }
        if ((item.options ?? []).length > 0) return item.options ?? [];
        const def = LoiFormAnswerTypeList.find((d) => d.id === item.answerType);
        return def?.options ?? [];
    };

    const renderValueInput = (item: LegalIssueItem) => {
        const value = item.value ?? '';
        const isInvalid = invalidItemIds?.has(item.id) ?? false;
        const common = {
            value,
            'aria-label': item.name,
        };
        switch (item.answerType) {
            case LoiFormAnswerType.Number:
                return (
                    <Input
                        type="number"
                        {...common}
                        disabled={readOnly}
                        onChange={(e) =>
                            handleValueChange(
                                item.id,
                                e.target.value === '' ? null : e.target.value
                            )
                        }
                        className="form-control loi-form-input"
                        invalid={isInvalid}
                    />
                );
            case LoiFormAnswerType.Date:
                return (
                    <Input
                        type="date"
                        {...common}
                        disabled={readOnly}
                        onChange={(e) =>
                            handleValueChange(
                                item.id,
                                e.target.value === '' ? null : e.target.value
                            )
                        }
                        className="form-control loi-form-input"
                        invalid={isInvalid}
                    />
                );
            case LoiFormAnswerType.PurchaseAgreementType:
            case LoiFormAnswerType.LeaseRateUponExtension: {
                return (
                    <Input
                        type="select"
                        {...common}
                        disabled={readOnly}
                        onChange={(e) =>
                            handleValueChange(
                                item.id,
                                e.target.value === '' ? null : e.target.value
                            )
                        }
                        className="form-control loi-form-input"
                        invalid={isInvalid}>
                        <option value="">Select...</option>
                        {getSelectOptions(item).map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </Input>
                );
            }
            default:
                return (
                    <Input
                        type="text"
                        {...common}
                        disabled={readOnly}
                        onChange={(e) =>
                            handleValueChange(
                                item.id,
                                e.target.value === '' ? null : e.target.value
                            )
                        }
                        className="form-control loi-form-input"
                        invalid={isInvalid}
                    />
                );
        }
    };

    const renderValueReadOnly = (item: LegalIssueItem) => {
        const value = item.value ?? '';
        if (
            item.answerType === LoiFormAnswerType.PurchaseAgreementType ||
            item.answerType === LoiFormAnswerType.LeaseRateUponExtension
        ) {
            const opt = getSelectOptions(item).find((o) => o.value === value);
            return (
                <span className="loi-form-value-readonly">
                    {opt ? opt.label : value || ''}
                </span>
            );
        }
        return (
            <span className="loi-form-value-readonly">
                {value || ''}
            </span>
        );
    };

    const sectionsOrdered = [...sectionsToDisplay].sort(
        (a, b) => a.order - b.order
    );

    return (
        <div
            className={`loi-form${mode === LoiFormMode.BuyerProfile ? ' loi-form--buyer' : ''}`}>
            <div className="loi-form-grid loi-form-grid-header">
                <div className="loi-form-row loi-form-row-head">
                    <div className="loi-form-cell loi-form-cell-name">
                        Legal Issue
                    </div>
                    <div className="loi-form-cell loi-form-cell-value">
                        {isAdminViewMode ? 'Default Value' : 'Value'}
                    </div>
                    <div className="loi-form-cell loi-form-cell-guidance">
                        Guidance
                    </div>
                </div>
            </div>
            {sectionsOrdered.map((section) => {
                const items = getOrderedItemsForSection(
                    section.id,
                    legalIssues
                );
                if (items.length === 0) return null;
                const isOpen = openSectionId === section.id;
                const sectionHasError =
                    invalidItemIds != null &&
                    items.some(
                        (item) =>
                            invalidItemIds.has(item.id) &&
                            (isAdminViewMode ||
                                isLegalIssueVisibleByLogic(legalIssues, item))
                    );
                return (
                    <CollapsibleComponent
                        key={section.id}
                        isExpanded={isOpen}
                        onToggle={(expanded) =>
                            setOpenSectionId(expanded ? section.id : null)
                        }
                        validationError={sectionHasError}
                        header={section.name}>
                        <div className="loi-form-section-body">
                            <div className="loi-form-grid">
                                {
                                    items.reduce<{
                                        currentHeaderLevel: number;
                                        nodes: React.ReactNode[];
                                    }>(
                                        (acc, item) => {
                                            if (!isAdminViewMode && !isLegalIssueVisibleByLogic(legalIssues, item)) {
                                                return acc;
                                            }
                                            const level = item.headerLevel;
                                            if (level != null && level >= 1) {
                                                acc.currentHeaderLevel = level;
                                            }
                                            const rowClass =
                                                level === 1
                                                    ? 'loi-form-row-header level-1'
                                                    : level != null &&
                                                        level >= 2
                                                      ? `loi-form-row-subheader level-${Math.min(level, 5)}`
                                                      : 'loi-form-row-item';
                                            const itemIndentLevel = Math.min(
                                                acc.currentHeaderLevel,
                                                5
                                            );
                                            const itemUnderClass =
                                                rowClass === 'loi-form-row-item'
                                                    ? ` item-under-${itemIndentLevel}`
                                                    : '';
                                            const showValueInput =
                                                item.showValueInput !== false;
                                            acc.nodes.push(
                                                <div
                                                    key={item.id}
                                                    className={`loi-form-row ${rowClass}${itemUnderClass}`}>
                                                    <div className="loi-form-cell loi-form-cell-name">
                                                        <div className="loi-form-cell-name-with-tooltip">
                                                            <span>{item.name}</span>
                                                            {item.tooltip ? (
                                                                <InfoTooltip
                                                                    text={item.tooltip}
                                                                    idText={`loi-tooltip-${item.id}`}
                                                                    className="loi-form-name-tooltip"
                                                                />
                                                            ) : null}
                                                        </div>
                                                    </div>
                                                    <div className="loi-form-cell loi-form-cell-value">
                                                        {isAdminViewMode || displayAsLabels
                                                            ? renderValueReadOnly(item)
                                                            : showValueInput
                                                              ? renderValueInput(item)
                                                              : null}
                                                    </div>
                                                    <div className="loi-form-cell loi-form-cell-guidance">
                                                        {item.guidance ? (
                                                            <span className="loi-form-guidance-plain">
                                                                {item.guidance}
                                                            </span>
                                                        ) : null}
                                                    </div>
                                                </div>
                                            );
                                            return acc;
                                        },
                                        { currentHeaderLevel: 1, nodes: [] }
                                    ).nodes
                                }
                            </div>
                        </div>
                    </CollapsibleComponent>
                );
            })}
        </div>
    );
};

export default LegalIssuesForm;
